import MediaNavTabs from '@/components/MediaNavTabs';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera } from 'lucide-react';
import mediaService from '@/services/mediaService';
import { formatThaiDate } from '@/lib/utils';
import gallery01 from '/src/assets/gallery/01.jpg'; // Keep as fallback for cover images


const Images = () => {
  const navigate = useNavigate();

  // Add CSS animation
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .animate-fade-in-up {
        animation: fadeInUp 0.6s ease-out forwards;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const [activeCategory, setActiveCategory] = useState('all');
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle folder click to navigate to detail page
  const handleFolderClick = (folder) => {
    console.log('📁 Navigating to folder detail:', folder);
    navigate(`/folder/${folder.id}`);
  };

  // ดึงโฟลเดอร์จาก API
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('📁 Fetching folders for Images page...');
        const response = await mediaService.getAllMedia({
          type: 'folder',
          status: 'published'
        });

        if (response.success && response.data) {
          console.log('✅ Folders loaded:', response.data.length);
          setFolders(response.data);
        } else {
          throw new Error('Failed to fetch folders');
        }
      } catch (err) {
        console.error('❌ Error fetching folders:', err);
        setError(err.message);
        setFolders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFolders();
  }, []);



  const categories = [
    { id: 'all', name: 'ทั้งหมด' },
    { id: 'events', name: 'งานกิจกรรม' },
    { id: 'artworks', name: 'ผลงานศิลปะ' },
    { id: 'workshops', name: 'เวิร์คช็อป' }
  ];

  // แปลงข้อมูลโฟลเดอร์จาก API ให้ตรงกับรูปแบบ UI
  const imageItems = folders.map((folder, index) => ({
    id: folder.id,
    title: folder.name || `โฟลเดอร์ SACIT Symposium`,
    category: folder.event === 'sacit2025' ? 'events' : 'artworks',
    date: folder.date ? formatThaiDate(folder.date) : 'ไม่ระบุวันที่',
    image: folder.cover_image_url || gallery01, // ใช้ cover_image_url หรือ fallback
    featured: index === 0,
    isFolder: true, // เพิ่ม flag ว่าเป็นโฟลเดอร์
    itemsCount: folder.items_count || 0 // จำนวนรูปในโฟลเดอร์
  }));

  // Use only API data
  const displayItems = imageItems;


  const filteredItems = activeCategory === 'all'
    ? displayItems
    : displayItems.filter(item => item.category === activeCategory);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative mt-16 sm:mt-20">
        {/* Top Banner */}
        <div className="w-full py-16" style={{
          background: 'linear-gradient(90deg, #BFB4EE 0%, #B3FFD1 100%)'
        }}>
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-center" style={{
              color: '#533192',
              fontFamily: 'Prompt',
              fontSize: 'clamp(1.6rem, 5vw, 3rem)',
              fontStyle: 'normal',
              fontWeight: '700',
              lineHeight: 'normal'
            }}>
              สื่อและข่าวสาร
            </h1>
          </div>
        </div>

        <MediaNavTabs />
        <div className="w-full h-1.5" style={{ background: '#533193' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-6 sm:mb-8" style={{ fontFamily: 'Prompt', color: '#333' }}>
          ภาพ
        </h2>

        {loading && <div className="h-48 flex items-center justify-center text-gray-500">กำลังโหลด...</div>}
        {error && <div className="text-center py-12 text-red-500">{error}</div>}

        {!loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                onClick={() => item.isFolder && handleFolderClick(item)}
                className="relative rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 cursor-pointer"
              >
                <div className="absolute inset-0">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-purple-900/80" />
                </div>
                <div className="relative h-[280px] sm:h-[360px] md:h-[420px] flex flex-col justify-end p-4 sm:p-5 md:p-6">
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs border border-white/60 text-white px-3 py-1 rounded-full" style={{ fontFamily: 'Prompt' }}>
                        {item.isFolder ? 'โฟลเดอร์ภาพ' : 'ภาพ'}
                      </span>
                      {item.isFolder && (
                        <span className="text-xs bg-white/20 text-white px-3 py-1 rounded-full inline-flex items-center gap-1.5" style={{ fontFamily: 'Prompt' }}>
                          <Camera className="w-3 h-3" />
                          {item.itemsCount} รูป
                        </span>
                      )}
                    </div>
                    <h3 className="text-base sm:text-lg md:text-xl font-bold text-white line-clamp-2" style={{ fontFamily: 'Prompt' }}>
                      {item.title}
                    </h3>
                    <div className="flex justify-end">
                      <span className="w-9 h-9 sm:w-11 sm:h-11 bg-purple-600 rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors">
                        <span className="w-0 h-0 border-l-[6px] sm:border-l-[8px] border-l-white border-t-[4px] sm:border-t-[6px] border-t-transparent border-b-[4px] sm:border-b-[6px] border-b-transparent ml-0.5" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {filteredItems.length === 0 && (
              <div className="col-span-full py-14 text-center text-gray-500">ยังไม่มีรูปภาพ</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Images; 